import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PaystackPop from "@paystack/inline-js";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import {
  useLazyGetAiCreditsBalanceQuery,
  usePurchaseAiCreditsMutation,
  useVerifyAiCreditsMutation,
} from "../../app/subscriptionApiSlice";
import toast from "../../helpers/toast";

const EMPTY_FW_CONFIG = {
  public_key: "",
  tx_ref: "placeholder",
  amount: 0,
  currency: "NGN",
  customer: { email: "" },
  customizations: { title: "SchoolApp" },
};

function openCheckoutPopup(url) {
  const width = 520;
  const height = 760;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  window.open(url, "_blank", `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`);
}

/**
 * TeacherAiWalletChip — shows the teacher's personal AI credits balance as a
 * compact chip. Clicking it opens a dialog to purchase more credits.
 * Designed to sit in the LmsHeader toolbar.
 */
export default function TeacherAiWalletChip() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [credits, setCredits] = useState(50);
  const [gateway, setGateway] = useState("paystack");
  const [pendingRef, setPendingRef] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [fwPendingConfig, setFwPendingConfig] = useState(null);
  const fwPendingRef = useRef(null);

  const [fetchBalance, { isFetching }] = useLazyGetAiCreditsBalanceQuery();
  const [purchaseAiCredits] = usePurchaseAiCreditsMutation();
  const [verifyAiCredits] = useVerifyAiCreditsMutation();

  const balanceCardBackground = theme.palette.mode === "dark"
    ? "rgba(76, 175, 80, 0.16)"
    : "#f0fff4";
  const balanceCardBorder = theme.palette.mode === "dark"
    ? "rgba(129, 199, 132, 0.28)"
    : "rgba(76, 175, 80, 0.16)";
  const infoTextColor = theme.palette.mode === "dark"
    ? "text.primary"
    : "text.secondary";

  const handleFlutterPayment = useFlutterwave(fwPendingConfig || EMPTY_FW_CONFIG);

  // Trigger Flutterwave popup when config is ready
  useEffect(() => {
    if (fwPendingConfig && fwPendingRef.current) {
      const onSuccess = fwPendingRef.current;
      handleFlutterPayment({
        callback: (response) => {
          closePaymentModal();
          fwPendingRef.current = null;
          setFwPendingConfig(null);
          onSuccess(response.tx_ref);
        },
        onClose: () => {
          fwPendingRef.current = null;
          setFwPendingConfig(null);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fwPendingConfig]);

  const openInlinePayment = useCallback((result, onVerifySuccess) => {
    const config = JSON.parse(JSON.stringify(result.config));
    const gatewayKey = result.gateway === "paystack" ? config.key : config.public_key;
    if (!gatewayKey) {
      const other = result.gateway === "paystack" ? "Flutterwave" : "Paystack";
      toast.error(`${result.gateway === "paystack" ? "Paystack" : "Flutterwave"} is not available. Please select ${other} instead.`);
      return;
    }
    if (result.gateway === "paystack") {
      const popup = new PaystackPop();
      popup.newTransaction({
        ...config,
        onSuccess: (tx) => onVerifySuccess(tx.reference),
        onCancel: () => toast.info("Payment cancelled"),
      });
    } else {
      fwPendingRef.current = onVerifySuccess;
      setFwPendingConfig(config);
    }
  }, []);

  const loadBalance = useCallback(() => {
    fetchBalance()
      .unwrap()
      .then((d) => setBalance(d?.balance ?? 0))
      .catch(() => setBalance(null));
  }, [fetchBalance]);

  // Fetch balance on mount
  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  const handleOpen = () => {
    loadBalance();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setPendingRef(null);
    setFwPendingConfig(null);
    fwPendingRef.current = null;
  };

  const handlePay = async () => {
    if (credits < 50) {
      toast.error("Minimum purchase is 50 credits (₦500).");
      return;
    }
    setPurchasing(true);
    try {
      const result = await purchaseAiCredits({ credits, gateway }).unwrap();
      try {
        openInlinePayment(result, async (reference) => {
          try {
            const r = await verifyAiCredits({ reference, gateway }).unwrap();
            toast.success(r.message || "AI credits added!");
            setBalance(r.balance ?? balance);
            setPendingRef(null);
            handleClose();
          } catch (err) {
            toast.error(err?.message || "Payment verification failed.");
          }
        });
      } catch (inlineErr) {
        if (result.gateway === "paystack" && result.payment_url) {
          setPendingRef(result.reference);
          openCheckoutPopup(result.payment_url);
          toast.info("Inline checkout could not start. Complete payment in the popup, then click Verify.");
        } else {
          throw inlineErr;
        }
      }
    } catch (err) {
      toast.error(err?.message || "Could not initialize payment.");
    } finally {
      setPurchasing(false);
    }
  };

  const handleVerify = async () => {
    if (!pendingRef) return;
    setPurchasing(true);
    try {
      const r = await verifyAiCredits({ reference: pendingRef, gateway: "paystack" }).unwrap();
      toast.success(r.message || "AI credits added!");
      setBalance(r.balance ?? balance);
      setPendingRef(null);
      handleClose();
    } catch (err) {
      toast.error(err?.message || "Payment verification failed.");
    } finally {
      setPurchasing(false);
    }
  };

  // Don't render if balance couldn't be fetched (user has no teacher profile)
  if (balance === null) return null;

  return (
    <>
      <Chip
        icon={
          isFetching
            ? <CircularProgress size={12} sx={{ color: "inherit" }} />
            : <AutoAwesomeIcon sx={{ fontSize: "14px !important" }} />
        }
        label={isFetching ? "…" : `${Number(balance).toLocaleString()} AI credits`}
        onClick={handleOpen}
        size="small"
        sx={{
          backgroundColor: "rgba(255, 165, 52, 0.15)",
          color: "#FFA534",
          fontWeight: 600,
          cursor: "pointer",
          border: "1px solid rgba(255, 165, 52, 0.4)",
          mr: 1,
          "&:hover": { backgroundColor: "rgba(255, 165, 52, 0.25)" },
        }}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundImage: "none",
            backgroundColor: "background.paper",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          My AI Credits
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              mb: 2,
              backgroundColor: balanceCardBackground,
              border: `1px solid ${balanceCardBorder}`,
              borderRadius: 2,
            }}
          >
            <AutoAwesomeIcon sx={{ color: "success.main" }} />
            <Box>
              <Typography variant="caption" color={infoTextColor}>
                CURRENT BALANCE
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {Number(balance).toLocaleString()} credits
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" color={infoTextColor} sx={{ mb: 2 }}>
            ₦10 = 1 credit. Used for AI comments (1 credit/student) and AI
            generated questions (1 credit each). Minimum purchase: 50 credits.
          </Typography>

          <TextField
            label="Credits to purchase"
            type="number"
            fullWidth
            size="small"
            value={credits}
            onChange={(e) => setCredits(Number(e.target.value))}
            inputProps={{ min: 50, step: 50 }}
            helperText={`= ₦${(credits * 10).toLocaleString()}`}
            sx={{ mb: 2 }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Payment Gateway</InputLabel>
            <Select
              value={gateway}
              label="Payment Gateway"
              onChange={(e) => setGateway(e.target.value)}
            >
              <MenuItem value="paystack">Paystack</MenuItem>
              <MenuItem value="flutterwave">Flutterwave</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
          <Button onClick={handleClose} size="small" sx={{ color: "text.secondary" }}>
            Close
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={
              purchasing ? <CircularProgress size={14} color="inherit" /> : <CreditCardOutlinedIcon />
            }
            onClick={handlePay}
            disabled={purchasing}
          >
            {purchasing ? "Processing…" : "Purchase"}
          </Button>
          {pendingRef && (
            <Button
              variant="outlined"
              color="success"
              size="small"
              onClick={handleVerify}
              disabled={purchasing}
            >
              I've Paid — Verify
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
